import { BrowserCookieSession, type ISessionStore } from "@mc/auth-client"
import { ApiError } from "./errorMessages.js"

/**
 * The single point of contact between apps/web and the Gateway (see
 * BrowserCookieSession's own doc comment in @mc/auth-client: "the single
 * point of contact the rest of apps/web talks to"). No other file in this
 * app should call fetch() against the Gateway directly, touch
 * document.cookie, or assume how the session travels over the wire.
 *
 * Today (browser, ISessionStore = BrowserCookieSession) that means
 * `credentials: "include"`, since the Gateway sets the JWT in an httpOnly
 * cookie the browser attaches automatically. A future
 * TauriSecureStorageSession would return a real token from getToken(),
 * and this file — only this file — would switch to an Authorization
 * header instead. Callers never know or care which transport is active.
 */
// Fallback matches infra/quadlets/gateway.container's PublishPort
// (4127:3000 -- host port 4127, container-internal port 3000, see that
// file's comment for why they differ deliberately). This fallback only
// applies when VITE_GATEWAY_URL truly isn't set at build time; the real
// fix for that gap is passing it as a build arg (see apps/web/Dockerfile
// and infra/scripts/containers.sh), not relying on this string staying
// in sync by hand forever -- but it's kept accurate as a safety net.
const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? "http://localhost:4127"

let sessionStore: ISessionStore = new BrowserCookieSession()

/** Test/Tauri hook: swap the session transport without touching any caller. */
export function configureSessionStore(store: ISessionStore): void {
  sessionStore = store
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  body?: unknown
  /** Skip the automatic silent-refresh-and-retry on a 401. Used by the
   *  refresh call itself, to avoid an infinite loop. */
  skipRefresh?: boolean
}

let refreshInFlight: Promise<boolean> | null = null

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await sendRequest(path, options)

  if (response.status === 401 && !options.skipRefresh) {
    const refreshed = await refreshSessionOnce()
    if (refreshed) {
      const retry = await sendRequest(path, options)
      return parseResponse<T>(retry)
    }
  }

  return parseResponse<T>(response)
}

async function sendRequest(path: string, options: RequestOptions): Promise<Response> {
  const token = await sessionStore.getToken()
  const headers: Record<string, string> = {}
  if (options.body !== undefined) headers["Content-Type"] = "application/json"
  if (token) headers.Authorization = `Bearer ${token}`

  return fetch(`${GATEWAY_URL}${path}`, {
    method: options.method ?? "GET",
    headers,
    credentials: token ? "omit" : "include",
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })
}

/** Coalesces concurrent 401s into a single refresh call instead of firing
 *  one refresh per failed request. */
function refreshSessionOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = sendRequest("/api/v1/auth/refresh", { method: "POST", skipRefresh: true })
      .then((res) => res.ok)
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T

  const data = await response.json().catch(() => null)

  if (!response.ok) {
    const code = data?.code ?? "UNKNOWN_ERROR"
    const message = data?.message ?? "Request failed"
    throw new ApiError(code, message, response.status)
  }

  return data as T
}
